import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SortOrder } from 'antd/es/table/interface';
import { RequestData } from '@ant-design/pro-table';
import { ProColumns } from '@ant-design/pro-table/es/typing';
import clsx from 'clsx';
import dayjs from 'dayjs';
import Table from '../../Common/Table';
import {
  capitalizeFirstLetter,
  getSorterParams,
  queryFilterParams,
} from '../../../utils';
import { useMessageError } from '../../../hooks/common';
import {
  ActionEnum, LogEntityEnum, LogTableRow, useLogsTableGet,
} from '../../../hooks/api/logs';
import LogExpandedRow from './ExpandedRow';
import { dateTimeFormat } from '../../../contstant';

import styles from './index.module.scss';

const renderExpandedRow = (record: LogTableRow) => (
  <LogExpandedRow record={record} />
);

const isExpandableRow = (record: LogTableRow) => (
  Boolean(record.before || record.after)
);

const columns: ProColumns<LogTableRow>[] = [
  {
    title: 'Entity',
    dataIndex: 'entityType',
    key: 'entityType',
    hideInSearch: false,
    valueEnum: LogEntityEnum,
    sorter: false,
    renderText: (text) => capitalizeFirstLetter(text),
  },
  {
    title: 'Action',
    dataIndex: 'action',
    key: 'action',
    hideInSearch: false,
    sorter: false,
    valueEnum: ActionEnum,
    renderText: (text) => capitalizeFirstLetter(text),
  },
  {
    title: 'Request to MBS',
    dataIndex: 'requestToMbs',
    key: 'requestToMbs',
    hideInSearch: true,
    sorter: false,
    render: (_, entity) => (
      entity.requestToMbs?.body || entity.requestToMbs?.query ? 'Yes' : 'No'
    ),
  },
  /* {
    title: 'Request Url',
    dataIndex: 'requestUrl',
    key: 'requestUrl',
    hideInSearch: true,
    sorter: false,
  }, */
  {
    title: 'Performed by',
    dataIndex: 'user',
    key: 'userName',
    hideInSearch: false,
    sorter: false,
    render: (_, entity) => {
      const { user } = entity;
      const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

      return (
        <div className={styles.userCol}>
          {[
            { label: 'Name', value: fullName },
            { label: 'Email', value: user?.email || '' },
            { label: 'Role', value: capitalizeFirstLetter(user?.role || '') },
          ]
            .filter(({ value }) => Boolean(value))
            .map(({ label, value }) => (
              <div key={label}>
                <span className={styles.colLabel}>
                  {label}
                  :
                  {' '}
                </span>
                <span>{value}</span>
              </div>
            ))}
          {user?.id ? (
            <div>
              <Link to={`/users/${user?.id}/edit`}>Go to user page</Link>
            </div>
          ) : 'Unknown user.'}
        </div>
      );
    },
  },
  {
    title: 'Action timestamp',
    dataIndex: 'createdAt',
    key: 'createdAt',
    hideInSearch: true,
    sorter: true,
    renderText: (text) => dayjs(text).format(dateTimeFormat),
  },
  {
    key: 'createdAt',
    title: 'Action date',
    dataIndex: 'createdAt',
    valueType: 'dateRange',
    hideInTable: true,
    search: {
      transform: (value) => (value ? ({
        dateFrom: value[0],
        dateTo: value[1],
      }) : undefined),
    },
  },
];

export default function LogsContent(): React.ReactNode | null {
  const tariffsTableGet = useLogsTableGet();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(Date.now());

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<LogTableRow>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : '1',
      pageSize: pageSize ? `${pageSize}` : '10',
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return tariffsTableGet.fetch({
      ...newParams,
      name: newParams?.name || undefined,
    }).then((response) => {
      if (response) {
        const { data, total } = response;

        return ({
          data: (data || []).map(((item) => ({
            ...item,
            key: item.id,
          }))),
          success: true,
          total,
        });
      }

      return ({ data: [], success: false, total: 0 });
    });
  };

  useMessageError([tariffsTableGet]);

  return (
    <Table<LogTableRow>
      key={tableKey}
      resetKey={() => setTableKey(Date.now())}
      className={clsx('transparent')}
      headerTitle="Logs list"
      // toolBarRender={ToolBar}
      request={tableRequest}
      columns={columns}
      expandable={{
        expandedRowRender: renderExpandedRow,
        rowExpandable: isExpandableRow,
      }}
      search={{
        searchText: 'Filter',
        resetText: 'Clear',
        style: {},
      }}
      options={{
        search: false,
      /* {
        name: 'name',
      }, */
      }}
      columnsState={{
        persistenceKey: 'pro-table-logs',
        persistenceType: 'localStorage',
      }}
    />
  );
}
